import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle2, XCircle, AlertTriangle, Camera, CameraOff, Loader2, Wine, Ticket, History, Trash2, Printer, PrinterCheck, Check } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { redeemCode, type RedeemResult } from "@/lib/redeem.functions";
import { Html5Qrcode } from "html5-qrcode";
import {
  connectPrinter,
  forgetPrinter,
  getSavedPrinterName,
  isBluetoothPrintingSupported,
  printDrinkReceipt,
} from "@/lib/bt-printer";
import { normalizeScannedCode } from "@/lib/scan-code";
import { toast } from "sonner";

export const Route = createFileRoute("/harikaradmin/scan")({
  head: () => ({ meta: [{ title: "Bilet Tarayıcı — JUËN Admin" }] }),
  component: ScanPage,
});

const HISTORY_KEY = "juen.scan.history.v2";

type HistoryEntry = {
  at: number;
  kind: "ticket" | "voucher";
  code: string;
  attendee_name: string;
  drink_name: string | null;
  ticket_type_name: string | null;
  event_name: string | null;
  delivered: boolean;
};

function ScanPage() {
  const { user, session, isAdmin, isWaiter, loading } = useAuth();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [result, setResult] = useState<RedeemResult | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [printerName, setPrinterName] = useState<string | null>(null);
  const [printerBusy, setPrinterBusy] = useState(false);
  const [autoPrint, setAutoPrint] = useState(true);
  const [bluetoothSupported, setBluetoothSupported] = useState<boolean | null>(null);
  const [printerHint, setPrinterHint] = useState<string | null>(null);
  const lastScannedRef = useRef<{ code: string; at: number } | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const manualInputRef = useRef<HTMLInputElement | null>(null);
  const containerId = "qr-scanner-region";

  const redeem = useServerFn(redeemCode);
  const allowed = isAdmin || isWaiter;

  // Load history from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) setHistory(JSON.parse(raw) as HistoryEntry[]);
    } catch {
      // ignore
    }
  }, []);

  const persistHistory = (next: HistoryEntry[]) => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const pushHistory = (entry: HistoryEntry) => {
    setHistory((prev) => {
      // Avoid duplicate undelivered entries for the same code
      const filtered = prev.filter((h) => !(h.code === entry.code && !h.delivered));
      const next = [entry, ...filtered].slice(0, 25);
      persistHistory(next);
      return next;
    });
  };

  const markDelivered = (code: string) => {
    setHistory((prev) => {
      const next = prev.map((h) => (h.code === code && !h.delivered ? { ...h, delivered: true } : h));
      persistHistory(next);
      return next;
    });
  };

  const clearHistory = () => {
    setHistory([]);
    try {
      localStorage.removeItem(HISTORY_KEY);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
    if (!loading && user && !allowed) void navigate({ to: "/dashboard" });
  }, [loading, user, allowed, navigate]);

  // Load remembered printer name
  useEffect(() => {
    setPrinterName(getSavedPrinterName());
    setBluetoothSupported(isBluetoothPrintingSupported());
  }, []);

  // Keep manual input focused so a Bluetooth HID barcode scanner can type
  // straight into it (and Enter submits the form).
  useEffect(() => {
    const focus = () => {
      const el = manualInputRef.current;
      if (!el) return;
      const active = document.activeElement;
      const tag = active?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      el.focus();
    };
    focus();
    const onClick = () => focus();
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const handleConnectPrinter = async () => {
    if (!isBluetoothPrintingSupported()) {
      const msg = "Bu tarayıcı Bluetooth cihaz seçme ekranını açmıyor. iPhone’da sayfayı Bluefy’nin adres çubuğundan https://juen.club/admin/scan olarak açın; Safari/Chrome veya Lovable önizleme içinden çalışmaz.";
      setBluetoothSupported(false);
      setPrinterHint(msg);
      toast.error("Bluefy içinde açın veya Web Bluetooth iznini kontrol edin.");
      return;
    }
    setPrinterHint(null);
    setPrinterBusy(true);
    const hintTimer = window.setTimeout(() => {
      setPrinterHint("Cihaz seçme ekranı açılmadıysa Bluefy ayarlarında Web Bluetooth iznini açın, sayfayı yenileyin ve yazıcının açık/eşleşmeye hazır olduğundan emin olun.");
    }, 2500);
    try {
      const { name } = await connectPrinter();
      setPrinterName(name);
      setBluetoothSupported(true);
      setPrinterHint(null);
      toast.success(`Yazıcı bağlandı: ${name}`);
    } catch (e) {
      const m = e instanceof Error ? e.message : "Bağlantı başarısız";
      setPrinterHint(m);
      toast.error(m);
    } finally {
      window.clearTimeout(hintTimer);
      setPrinterBusy(false);
    }
  };

  const handleForgetPrinter = () => {
    forgetPrinter();
    setPrinterName(null);
    toast.message("Yazıcı unutuldu");
  };

  const handleTestPrint = async () => {
    setPrinterBusy(true);
    try {
      await printDrinkReceipt({
        drinkName: "TEST FİŞİ",
        attendeeName: "Bar Test",
        eventName: "Bağlantı kontrolü",
        code: "TEST-0000",
        at: new Date(),
      });
      toast.success("Test fişi gönderildi");
    } catch (e) {
      const m = e instanceof Error ? e.message : "Yazdırma başarısız";
      toast.error(m);
    } finally {
      setPrinterBusy(false);
    }
  };

  const handleSubmit = async (rawCode: string) => {
    const code = normalizeScannedCode(rawCode);
    if (!code || !session?.access_token) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await redeem({
        data: { code, confirm: true },
        headers: {
          authorization: `Bearer ${session.access_token}`,
        },
      });
      setResult(res);
      // On successful redemption: log to history (delivered) and auto-print receipt
      if (res.ok && !res.preview) {
        pushHistory({
          at: Date.now(),
          kind: res.kind,
          code: res.item.code,
          attendee_name: res.item.attendee_name,
          drink_name: res.item.drink_name ?? null,
          ticket_type_name: res.item.ticket_type_name ?? null,
          event_name: res.item.event_name ?? null,
          delivered: true,
        });
        if (autoPrint && res.kind === "voucher" && printerName && res.item.drink_name) {
          setPrinterBusy(true);
          try {
            await printDrinkReceipt({
              drinkName: res.item.drink_name,
              attendeeName: res.item.attendee_name,
              eventName: res.item.event_name ?? null,
              code: res.item.code,
              at: new Date(),
            });
          } catch (e) {
            const m = e instanceof Error ? e.message : "Yazdırma başarısız";
            toast.error(`Yazıcı: ${m}`);
          } finally {
            setPrinterBusy(false);
          }
        }
      } else if (!res.ok && res.reason === "already_used" && res.kind === "voucher" && res.item) {
        if (autoPrint && printerName) {
          setPrinterBusy(true);
          try {
            await printDrinkReceipt({
              drinkName: res.item.drink_name ?? "Icecek",
              attendeeName: res.item.attendee_name,
              eventName: res.item.event_name ?? null,
              code: res.item.code,
              at: new Date(),
              alreadyUsed: true,
            });
          } catch (e) {
            const m = e instanceof Error ? e.message : "Yazdırma başarısız";
            toast.error(`Yazıcı: ${m}`);
          } finally {
            setPrinterBusy(false);
          }
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Bir hata oluştu";
      setResult({ ok: false, reason: "error", message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeliver = async (code: string) => {
    if (!session?.access_token) return;
    try {
      const res = await redeem({
        data: { code, confirm: true },
        headers: { authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok && !res.preview) {
        markDelivered(code);
        toast.success("Teslim edildi");
      } else if (!res.ok) {
        if (res.reason === "already_used") {
          markDelivered(code);
          toast.message("Bu kod zaten kullanılmış");
        } else {
          toast.error(res.message);
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Bir hata oluştu";
      toast.error(msg);
    }
  };

  const startScanner = async () => {
    setResult(null);
    try {
      const scanner = new Html5Qrcode(containerId, { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => {
          // Debounce duplicate decodes
          const now = Date.now();
          const last = lastScannedRef.current;
          if (last && last.code === decoded && now - last.at < 3000) return;
          lastScannedRef.current = { code: decoded, at: now };
          void handleSubmit(decoded);
        },
        () => {
          // ignore scan failures (every frame)
        },
      );
      setScanning(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Kamera açılamadı";
      setResult({ ok: false, reason: "error", message: msg });
      scannerRef.current = null;
    }
  };

  const stopScanner = async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      await s.stop();
      await s.clear();
    } catch {
      // noop
    }
    scannerRef.current = null;
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      void stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-10">
        {isAdmin && (
          <Link to="/harikaradmin" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Admin
          </Link>
        )}

        <h1 className="text-3xl font-bold tracking-tight">Bilet Tarayıcı</h1>
        <p className="mt-2 text-muted-foreground">QR kodu okutun veya kodu manuel girin. Her bilet yalnızca bir kez kullanılabilir.</p>

        {isAdmin && (
          <div className="mt-6">
            <AdminTabs />
          </div>
        )}

        {/* Result (top) */}
        {result && (
          <ResultCard
            result={result}
            onClear={() => setResult(null)}
          />
        )}

        {/* Printer status */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-card">
          <div className="flex items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-semibold">
              {printerName ? <PrinterCheck className="h-4 w-4 text-emerald-600" /> : <Printer className="h-4 w-4" />}
              Bluetooth Yazıcı
            </h2>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={autoPrint}
                onChange={(e) => setAutoPrint(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              Onayda otomatik yazdır
            </label>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {printerName ? (
              <>Bağlı: <span className="font-medium text-foreground">{printerName}</span></>
            ) : bluetoothSupported === null ? (
              "Bluetooth desteği kontrol ediliyor..."
            ) : bluetoothSupported ? (
              "Henüz bağlı yazıcı yok. Bara fişi otomatik basmak için yazıcıyı eşleştirin."
            ) : (
              "Bu tarayıcı Bluetooth cihaz seçme ekranını açmıyor. iPhone’da Bluefy kullanın ve sayfayı Bluefy adres çubuğundan açın."
            )}
          </p>
          {printerHint && (
            <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-muted-foreground">
              {printerHint}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => void handleConnectPrinter()} disabled={printerBusy}>
              {printerBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Printer className="mr-1.5 h-4 w-4" />}
              {printerName ? "Yeniden Bağla" : "Yazıcıyı Bağla"}
            </Button>
            {printerName && (
              <>
                <Button size="sm" variant="outline" onClick={() => void handleTestPrint()} disabled={printerBusy}>
                  Test Yazdır
                </Button>
                <Button size="sm" variant="ghost" onClick={handleForgetPrinter} disabled={printerBusy}>
                  Unut
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Manual entry */}
        <div className="mt-4 rounded-2xl border border-border bg-card p-5 shadow-card">
          <h2 className="font-semibold">Manuel kod / Barkod okuyucu</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Bluetooth barkod okuyucuyu telefon/tablete eşleştirin; tarama sonrası kod otomatik buraya yazılır ve Enter ile gönderilir.
          </p>
          <form
            className="mt-3 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const code = manualCode;
              setManualCode("");
              void handleSubmit(code);
            }}
          >
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="code" className="sr-only">Bilet kodu</Label>
              <Input
                id="code"
                ref={manualInputRef}
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="QR / barkod okutun veya yazın"
                autoComplete="off"
                spellCheck={false}
                autoFocus
                className="font-mono uppercase tracking-wider"
              />
            </div>
            <Button type="submit" disabled={submitting || !manualCode.trim()}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Doğrula"}
            </Button>
          </form>
        </div>


        {/* Scanner */}
        <div className="mt-4 rounded-2xl border border-border bg-card p-5 shadow-card">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold">Kamera</h2>
            {scanning ? (
              <Button variant="outline" size="sm" onClick={() => void stopScanner()}>
                <CameraOff className="mr-1.5 h-4 w-4" /> Durdur
              </Button>
            ) : (
              <Button size="sm" onClick={() => void startScanner()}>
                <Camera className="mr-1.5 h-4 w-4" /> Başlat
              </Button>
            )}
          </div>
          <div
            id={containerId}
            className="mt-4 aspect-square w-full overflow-hidden rounded-xl bg-black/80"
          />
          {!scanning && (
            <p className="mt-2 text-xs text-muted-foreground">Tarayıcıyı başlattığınızda kamera erişimi istenecek.</p>
          )}
        </div>

        {/* Recent deliveries */}
        <div className="mt-4 rounded-2xl border border-border bg-card p-5 shadow-card">
          <div className="flex items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-semibold">
              <History className="h-4 w-4" /> Son scan edilenler
            </h2>
            {history.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearHistory}>
                <Trash2 className="mr-1.5 h-4 w-4" /> Temizle
              </Button>
            )}
          </div>
          {history.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">Henüz scan edilen bir şey yok.</p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {history.map((h) => {
                const Icon = h.kind === "voucher" ? Wine : Ticket;
                const label = h.kind === "voucher"
                  ? (h.drink_name ?? "İçecek")
                  : (h.ticket_type_name ?? "Bilet");
                return (
                  <li key={`${h.code}-${h.at}`} className="flex items-center gap-3 py-2.5">
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        {label} <span className="text-muted-foreground">— {h.attendee_name}</span>
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {new Date(h.at).toLocaleTimeString("tr-TR")}
                        {h.event_name ? ` · ${h.event_name}` : ""}
                      </div>
                    </div>
                    {h.delivered ? (
                      <div
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600"
                        title="Teslim edildi"
                      >
                        <Check className="h-5 w-5" />
                      </div>
                    ) : (
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-9 w-9 shrink-0 rounded-full"
                        onClick={() => void handleDeliver(h.code)}
                        title="Teslim edildi olarak işaretle"
                      >
                        <Check className="h-5 w-5" />
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

      </div>
    </div>
  );
}

function ResultCard({
  result,
  onClear,
}: {
  result: RedeemResult;
  onClear: () => void;
}) {
  if (result.ok) {
    const isVoucher = result.kind === "voucher";
    const KindIcon = isVoucher ? Wine : Ticket;
    const isPreview = result.preview === true;
    const successTitle = isPreview
      ? isVoucher
        ? "Scan edildi — Fiş yazdırıldı"
        : "Bilet okundu"
      : isVoucher
        ? "Geçerli — İçecek teslim edildi"
        : "Geçerli — Giriş yapıldı";
    const borderClass = isPreview ? "border-primary/40 bg-primary/5" : "border-emerald-500/40 bg-emerald-500/10";
    const titleColor = isPreview ? "text-primary" : "text-emerald-600";
    return (
      <div className={`mt-6 overflow-hidden rounded-2xl border-2 p-5 ${borderClass}`}>
        <div className={`flex items-center gap-2 ${titleColor}`}>
          {isPreview ? <AlertTriangle className="h-6 w-6" /> : <CheckCircle2 className="h-6 w-6" />}
          <h3 className="text-lg font-bold">{successTitle}</h3>
          <KindIcon className="ml-auto h-5 w-5 opacity-70" />
        </div>
        <div className="mt-3 grid gap-1 text-sm">
          <Row label="Misafir" value={result.item.attendee_name} />
          {result.item.event_name && <Row label="Etkinlik" value={result.item.event_name} />}
          {result.item.ticket_type_name && <Row label="Bilet" value={result.item.ticket_type_name} />}
          {result.item.drink_name && <Row label="İçecek" value={result.item.drink_name} />}
          <Row label="Kod" value={result.item.code} mono />
          {result.item.used_at && (
            <Row label="Zaman" value={new Date(result.item.used_at).toLocaleString("tr-TR")} />
          )}
        </div>
        {!isVoucher && (result.item.door_min_topup_kurus ?? 0) > 0 && (
          <div className="mt-3 rounded-xl border-2 border-amber-500/60 bg-amber-500/15 p-3 text-sm">
            <div className="flex items-center gap-2 font-semibold text-amber-700">
              <AlertTriangle className="h-4 w-4" /> Minimum harcama limiti
            </div>
            <p className="mt-1 text-foreground/90">
              Hesabına girişte minimum
              {" "}<span className="font-bold">{((result.item.door_min_topup_kurus ?? 0) / 100).toLocaleString("tr-TR")} J</span> yükleme yapmanız gerekmektedir.
            </p>
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <Button variant="outline" size="sm" onClick={onClear}>Temizle</Button>
        </div>
      </div>
    );
  }

  const isAlreadyUsed = result.reason === "already_used";
  const isVoucher = result.kind === "voucher";
  const Icon = isAlreadyUsed ? AlertTriangle : XCircle;
  const colorClass = isAlreadyUsed
    ? "border-amber-500/40 bg-amber-500/10 text-amber-600"
    : "border-destructive/40 bg-destructive/10 text-destructive";
  const title = isAlreadyUsed
    ? isVoucher
      ? "Uyarı — Bu içecek kuponu daha önce kullanıldı"
      : "Uyarı — Bu bilet daha önce kullanıldı"
    : result.reason === "not_found"
      ? "Geçersiz — Kayıt bulunamadı"
      : result.reason === "forbidden"
        ? "Yetkisiz"
        : "Hata";

  return (
    <div className={`mt-6 overflow-hidden rounded-2xl border-2 p-5 ${colorClass.split(" ").slice(0, 2).join(" ")}`}>
      <div className={`flex items-center gap-2 ${colorClass.split(" ")[2]}`}>
        <Icon className="h-6 w-6" />
        <h3 className="text-lg font-bold">{title}</h3>
      </div>
      <p className="mt-2 text-sm text-foreground/90">{result.message}</p>
      {result.item && (
        <div className="mt-3 grid gap-1 text-sm">
          <Row label="Misafir" value={result.item.attendee_name} />
          {result.item.event_name && <Row label="Etkinlik" value={result.item.event_name} />}
          {result.item.ticket_type_name && <Row label="Bilet" value={result.item.ticket_type_name} />}
          {result.item.drink_name && <Row label="İçecek" value={result.item.drink_name} />}
          <Row label="Kod" value={result.item.code} mono />
        </div>
      )}
      <Button variant="outline" size="sm" className="mt-4" onClick={onClear}>Temizle</Button>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3 border-b border-border/50 py-1 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={`text-right font-medium ${mono ? "font-mono tracking-wider" : ""}`}>{value}</span>
    </div>
  );
}
